/**
 * AI 議題工作區 — 議題詳情頁（工作區）
 * 動態路由 /ee_research_topics/:topic_code
 *
 * Phase 0：4 個 Tab + 右側 AI 助理 Drawer
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Tabs, Tab, Button, Chip, IconButton, Tooltip, Typography,
  Paper, Card, CardContent, Divider, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, FormControlLabel, Switch,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon, SmartToy as AIIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, AutoAwesome as MagicIcon,
  PlayArrow as TestIcon, CloudDownload as FetchIcon,
  Article as ReportIcon, OpenInNew as OpenIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import AiWorkspaceDrawer from '../components/ai-workspace/AiWorkspaceDrawer';
import type { ActionMetaMap } from '../components/ai-workspace/types';
import MarkdownText from '../components/MarkdownText';
import { useMessage } from '../contexts/MessageContext';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import {
  Topic, TopicSource, SourceType, ResearchItem, Report,
  SourceCreate, ItemCreate, AiStatus,
} from '../types/research';
import {
  getTopicByCode,
  listSources, createSource, deleteSource, testSource, updateSource,
  listItems, createItem, updateItem, deleteItem, analyzeItem,
  fetchNow,
  listReports, generateReport, updateReport, deleteReport,
  workspaceChat, executeAction,
} from '../services/researchService';
import { formatLocal } from '../utils/dateHelper';

const FUNC_CODE = 'ee_research_topics';

// 議題模組的動作 chip 配色 + 中文名稱（傳給通用 drawer 用）
const RESEARCH_ACTION_META: ActionMetaMap = {
  add_source:            { label: '新增來源',     color: 'primary' },
  set_tag_dimensions:    { label: '設標籤維度',   color: 'info' },
  set_keywords:          { label: '設搜尋關鍵字', color: 'info' },
  update_topic_settings: { label: '更新議題設定', color: 'info' },
  fetch_now:             { label: '立即蒐集',     color: 'warning' },
  generate_report:       { label: '生成報告',     color: 'secondary' },
  translate_report:      { label: '翻譯報告',     color: 'secondary' },
  update_item_tags:      { label: '校正素材標籤', color: 'info' },
  test_source:           { label: '測試來源',     color: 'primary' },
};

const RESEARCH_STARTER_PROMPTS = [
  '幫我訂這個議題的標籤維度',
  '幫我找 5–8 個可信的中文與英文資料來源',
  '現有來源就緒，立即蒐集一輪',
  '幫我寫一份全球走向報告，2000 字',
];

const fmt = (iso?: string | null) => iso ? formatLocal(iso) : '—';
const fmtDate = (s?: string | null) => s || '—';

/** 合併 user_tags 與 ai_tags：user_tags 覆蓋 ai_tags（user 校正後優先） */
const mergedTags = (item: ResearchItem): Record<string, string | null> => {
  return { ...(item.ai_tags || {}), ...(item.user_tags || {}) };
};

/** 該維度的值是否來自使用者校正（用於 chip 顯示樣式） */
const isUserTag = (item: ResearchItem, dim: string): boolean => {
  return !!(item.user_tags && item.user_tags[dim] != null);
};

const aiStatusColor = (s: AiStatus): 'default' | 'info' | 'success' | 'error' => {
  if (s === 'done') return 'success';
  if (s === 'failed') return 'error';
  if (s === 'processing') return 'info';
  return 'default';
};

const ResearchTopicDetailPage: React.FC = () => {
  const { topicCode } = useParams<{ topicCode: string }>();
  const navigate = useNavigate();
  const { showSuccess, showApiError } = useMessage();

  const [topic, setTopic] = useState<Topic | null>(null);
  const [tab, setTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingTopic, setLoadingTopic] = useState(false);
  // AI 動作執行成功後 bump，讓子 Tab 透過 key 重新 mount 觸發資料重抓
  const [refreshKey, setRefreshKey] = useState(0);

  const reloadTopic = useCallback(async () => {
    if (!topicCode) return;
    setLoadingTopic(true);
    try {
      const t = await getTopicByCode(topicCode);
      setTopic(t);
      logView(FUNC_CODE, { action: 'view_topic', topic_code: topicCode, topic_id: t.id });
    } catch (err: any) {
      logView(FUNC_CODE, { action: 'view_topic', topic_code: topicCode },
              String(err?.message || err));
      showApiError(err);
    } finally {
      setLoadingTopic(false);
    }
  }, [topicCode, showApiError]);

  useEffect(() => { reloadTopic(); }, [reloadTopic]);

  const handleAiActionExecuted = useCallback(() => {
    setRefreshKey(k => k + 1);
    reloadTopic();
  }, [reloadTopic]);

  if (!topic) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        {loadingTopic ? <CircularProgress /> : <Alert severity="warning">找不到議題</Alert>}
      </Box>
    );
  }

  // 主內容區寬度：drawer 開時讓出 380px
  const contentSx = drawerOpen ? { mr: '380px', transition: 'margin-right 0.2s ease' } : {};

  return (
    <Box>
      {/* 頁頭 */}
      <Box sx={contentSx}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
          <IconButton size="small" onClick={() => navigate('/ee_research_topics')}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {topic.topic_name}
              <Chip label={topic.topic_code} size="small" sx={{ ml: 1, fontFamily: 'monospace' }} />
            </Typography>
            {topic.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {topic.description}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {(topic.tag_dimensions || []).map(d => (
                <Chip key={d} label={d} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
          <Button
            variant={drawerOpen ? 'contained' : 'outlined'}
            size="small"
            startIcon={<AIIcon />}
            onClick={() => setDrawerOpen(o => !o)}
          >
            AI 助理
          </Button>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="素材" />
          <Tab label="時間軸" />
          <Tab label="來源" />
          <Tab label="報告" />
        </Tabs>

        {tab === 0 && <ItemsTab key={`items-${refreshKey}`} topic={topic} reloadTopic={reloadTopic} />}
        {tab === 1 && <TimelineTab key={`timeline-${refreshKey}`} topic={topic} />}
        {tab === 2 && <SourcesTab key={`sources-${refreshKey}`} topic={topic} />}
        {tab === 3 && <ReportsTab key={`reports-${refreshKey}`} topic={topic} />}
      </Box>

      <AiWorkspaceDrawer
        parentId={topic.id}
        parentName={topic.topic_name}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        services={{ chat: workspaceChat, executeAction }}
        actionMeta={RESEARCH_ACTION_META}
        starterPrompts={RESEARCH_STARTER_PROMPTS}
        onActionExecuted={handleAiActionExecuted}
      />
    </Box>
  );
};

// ============================================================
// Tab 1：素材
// ============================================================
const ItemsTab: React.FC<{ topic: Topic; reloadTopic: () => void }> = ({ topic, reloadTopic }) => {
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');
  const [items, setItems] = useState<ResearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<ItemCreate>({ item_type: 'url', title: '' });
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState<number | null>(null);
  const [fetching, setFetching] = useState(false);

  // 標籤編輯對話框
  const [editTagsItem, setEditTagsItem] = useState<ResearchItem | null>(null);
  const [tagsForm, setTagsForm] = useState<Record<string, string>>({});
  const [savingTags, setSavingTags] = useState(false);

  // 標籤篩選（客戶端）
  const [filterDim, setFilterDim] = useState<string>('');
  const [filterValue, setFilterValue] = useState<string>('');

  const reload = useCallback(async () => {
    setLoading(true);
    try { setItems(await listItems(topic.id)); }
    catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [topic.id, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  // 從所有素材聚合出每個維度出現過的值（給篩選下拉用）
  const dimValues = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const dim of topic.tag_dimensions || []) map[dim] = new Set();
    for (const item of items) {
      const tags = mergedTags(item);
      for (const dim of topic.tag_dimensions || []) {
        const v = tags[dim];
        if (v) map[dim].add(String(v));
      }
    }
    return map;
  }, [items, topic.tag_dimensions]);

  // 篩選後的素材
  const visibleItems = useMemo(() => {
    if (!filterDim || !filterValue) return items;
    return items.filter(it => {
      const tags = mergedTags(it);
      return String(tags[filterDim] || '') === filterValue;
    });
  }, [items, filterDim, filterValue]);

  const openEditTags = (item: ResearchItem) => {
    setEditTagsItem(item);
    const merged = mergedTags(item);
    const initial: Record<string, string> = {};
    for (const dim of topic.tag_dimensions || []) {
      initial[dim] = merged[dim] != null ? String(merged[dim]) : '';
    }
    setTagsForm(initial);
  };

  const handleSaveTags = async () => {
    if (!editTagsItem) return;
    setSavingTags(true);
    // 空字串視為 null（清除）
    const userTags: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(tagsForm)) {
      userTags[k] = v.trim() ? v.trim() : null;
    }
    try {
      await updateItem(editTagsItem.id, { user_tags: userTags });
      logUpdate(FUNC_CODE,
        { action: 'edit_item_tags', item_id: editTagsItem.id, before: editTagsItem.user_tags },
        { action: 'edit_item_tags', item_id: editTagsItem.id, after: userTags });
      setEditTagsItem(null);
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE,
        { action: 'edit_item_tags', item_id: editTagsItem.id },
        { user_tags: userTags }, String(err?.message || err));
      showApiError(err);
    }
    finally { setSavingTags(false); }
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const created = await createItem(topic.id, form);
      logCreate(FUNC_CODE, { action: 'create_item', ...created });
      showSuccess('SYS020001', { name: form.title });
      setAddOpen(false);
      setForm({ item_type: 'url', title: '' });
      reload();
      // 自動觸發 AI 解析
      try {
        await analyzeItem(created.id);
      } catch (e) {
        // 解析失敗不擋
      }
      reload();
    } catch (err: any) {
      logCreate(FUNC_CODE, { action: 'create_item', topic_id: topic.id, ...form },
                String(err?.message || err));
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: ResearchItem) => {
    if (!window.confirm(`確定刪除「${item.title}」？`)) return;
    try {
      await deleteItem(item.id);
      logDelete(FUNC_CODE, { action: 'delete_item', item_id: item.id, title: item.title });
      reload();
    } catch (err: any) {
      logDelete(FUNC_CODE, { item_id: item.id, title: item.title },
                String(err?.message || err));
      showApiError(err);
    }
  };

  const handleAnalyze = async (item: ResearchItem) => {
    setAnalyzing(item.id);
    const intent = { action: 'analyze_item', item_id: item.id, title: item.title };
    try {
      await analyzeItem(item.id);
      logUpdate(FUNC_CODE, intent, { ...intent, ok: true });
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE, intent, intent, String(err?.message || err));
      showApiError(err);
    }
    finally { setAnalyzing(null); }
  };

  const handleFetchNow = async () => {
    setFetching(true);
    const intent = { action: 'fetch_now', topic_id: topic.id, topic_code: topic.topic_code };
    try {
      const stat = await fetchNow(topic.id);
      logCreate(FUNC_CODE, { ...intent, result: stat });
      showSuccess('SYS020001', {
        name: `蒐集完成：新增 ${stat.items_created} 筆、跳過重複 ${stat.deduped_skip}、低相關 ${stat.low_relevance_skip}`,
      });
      reload();
      reloadTopic();
    } catch (err: any) {
      logCreate(FUNC_CODE, intent, String(err?.message || err));
      showApiError(err);
    }
    finally { setFetching(false); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        {canCreate && (
          <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => setAddOpen(true)}>
            新增素材
          </Button>
        )}
        {canCreate && (
          <Button size="small" variant="outlined"
                  startIcon={fetching ? <CircularProgress size={14} /> : <FetchIcon />}
                  onClick={handleFetchNow} disabled={fetching}>
            立即蒐集
          </Button>
        )}
        <Button size="small" variant="outlined"
                startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={reload} disabled={loading}>
          重新整理
        </Button>

        {/* 標籤篩選 */}
        {(topic.tag_dimensions || []).length > 0 && (
          <>
            <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>標籤維度</InputLabel>
              <Select label="標籤維度" value={filterDim}
                      onChange={e => { setFilterDim(e.target.value); setFilterValue(''); }}>
                <MenuItem value="">不篩選</MenuItem>
                {(topic.tag_dimensions || []).map(d => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }} disabled={!filterDim}>
              <InputLabel>值</InputLabel>
              <Select label="值" value={filterValue}
                      onChange={e => setFilterValue(e.target.value)}>
                <MenuItem value="">全部</MenuItem>
                {filterDim && Array.from(dimValues[filterDim] || []).sort().map(v => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(filterDim || filterValue) && (
              <Button size="small" onClick={() => { setFilterDim(''); setFilterValue(''); }}>
                清除篩選
              </Button>
            )}
          </>
        )}

        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {filterDim && filterValue
            ? `篩選：${visibleItems.length} / ${items.length} 筆`
            : `共 ${items.length} 筆`}
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell width={70}>素材 ID</TableCell>
              <TableCell>標題 / 摘要</TableCell>
              <TableCell width={100}>來源</TableCell>
              <TableCell width={100}>事件日期</TableCell>
              <TableCell width={80} align="right">相關度</TableCell>
              <TableCell width={80}>狀態</TableCell>
              <TableCell width={120} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '載入中…' : (items.length === 0 ? '尚無素材' : '篩選後無資料')}
                </TableCell>
              </TableRow>
            ) : visibleItems.map(item => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    #{item.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.source_url ? (
                      <a href={item.source_url} target="_blank" rel="noopener noreferrer"
                         style={{ color: 'inherit', textDecoration: 'none' }}>
                        {item.title} <OpenIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                      </a>
                    ) : item.title}
                  </Typography>
                  {item.ai_summary && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                      {item.ai_summary.slice(0, 200)}{item.ai_summary.length > 200 ? '…' : ''}
                    </Typography>
                  )}
                  {(() => {
                    const tags = mergedTags(item);
                    const entries = Object.entries(tags).filter(([_, v]) => v);
                    if (entries.length === 0) return null;
                    return (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {entries.map(([k, v]) => (
                          <Chip
                            key={k}
                            label={`${k}: ${v}`}
                            size="small"
                            variant={isUserTag(item, k) ? 'filled' : 'outlined'}
                            color={isUserTag(item, k) ? 'primary' : 'default'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Stack>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Chip label={item.source_type} size="small" variant="outlined"
                        sx={{ fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{fmtDate(item.ai_event_date)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption">
                    {item.ai_relevance_score != null
                      ? `${(item.ai_relevance_score * 100).toFixed(0)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={item.ai_status} size="small"
                        color={aiStatusColor(item.ai_status)} />
                  {item.ai_error && (
                    <Tooltip title={item.ai_error}>
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        錯誤
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  {canUpdate && (
                    <Tooltip title="編輯標籤">
                      <span>
                        <IconButton size="small"
                                    disabled={(topic.tag_dimensions || []).length === 0}
                                    onClick={() => openEditTags(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canUpdate && (
                    <Tooltip title={item.ai_status === 'done' ? '重跑 AI' : 'AI 解析'}>
                      <span>
                        <IconButton size="small" onClick={() => handleAnalyze(item)}
                                    disabled={analyzing === item.id}>
                          {analyzing === item.id ? <CircularProgress size={16} />
                            : <MagicIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="刪除">
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增素材 */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新增素材</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>類型</InputLabel>
              <Select label="類型" value={form.item_type}
                      onChange={e => setForm({ ...form, item_type: e.target.value as any })}>
                <MenuItem value="url">URL（連結）</MenuItem>
                <MenuItem value="text">純文字</MenuItem>
                <MenuItem value="file">檔案路徑（手填）</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="標題" required size="small"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />
            {form.item_type === 'url' && (
              <TextField
                label="URL" size="small" required
                value={form.source_url || ''}
                onChange={e => setForm({ ...form, source_url: e.target.value })}
              />
            )}
            {form.item_type === 'file' && (
              <TextField
                label="檔案路徑" size="small" required
                value={form.file_path || ''}
                onChange={e => setForm({ ...form, file_path: e.target.value })}
                helperText="Phase 0 暫不支援上傳；先填路徑或留空，把內容貼到下方"
              />
            )}
            <TextField
              label="原文 / 內容" multiline rows={6} size="small"
              value={form.raw_text || ''}
              onChange={e => setForm({ ...form, raw_text: e.target.value })}
              helperText="可選；URL 類型若不填則 AI 只能依標題判讀"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>取消</Button>
          <Button variant="contained" disabled={saving || !form.title}
                  onClick={handleAdd}>
            {saving ? <CircularProgress size={18} /> : '建立並 AI 解析'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編輯標籤 */}
      <Dialog open={!!editTagsItem} onClose={() => setEditTagsItem(null)}
              maxWidth="sm" fullWidth>
        <DialogTitle>
          編輯標籤
          {editTagsItem && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              #{editTagsItem.id} {editTagsItem.title.slice(0, 60)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {editTagsItem && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                空欄位代表清除使用者校正，恢復顯示 AI 自動標籤。
                填值會覆蓋 AI 標籤（chip 顯示為實心藍）。
              </Alert>
              <Stack spacing={2}>
                {(topic.tag_dimensions || []).map(dim => {
                  const ai = editTagsItem.ai_tags?.[dim];
                  return (
                    <TextField
                      key={dim}
                      label={dim}
                      size="small"
                      value={tagsForm[dim] || ''}
                      onChange={e => setTagsForm({ ...tagsForm, [dim]: e.target.value })}
                      helperText={ai != null && ai !== ''
                        ? `AI 建議：${ai}` : 'AI 未填'}
                    />
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTagsItem(null)}>取消</Button>
          <Button variant="contained" disabled={savingTags} onClick={handleSaveTags}>
            {savingTags ? <CircularProgress size={18} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================
// Tab 2：時間軸
// ============================================================
const TimelineTab: React.FC<{ topic: Topic }> = ({ topic }) => {
  const { showApiError } = useMessage();
  const [items, setItems] = useState<ResearchItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    listItems(topic.id, { page_size: 200 })
      .then(setItems)
      .catch(showApiError)
      .finally(() => setLoading(false));
  }, [topic.id, showApiError]);

  // 依 ai_event_date 排序（無日期排最後）
  const sorted = [...items].sort((a, b) => {
    if (!a.ai_event_date && !b.ai_event_date) return 0;
    if (!a.ai_event_date) return 1;
    if (!b.ai_event_date) return -1;
    return b.ai_event_date.localeCompare(a.ai_event_date);
  });

  return (
    <Box>
      {loading && <CircularProgress size={20} />}
      {!loading && sorted.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>無素材</Typography>
      )}
      <Stack spacing={1}>
        {sorted.map(item => (
          <Card key={item.id} variant="outlined">
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{
                  minWidth: 90, fontFamily: 'monospace',
                  color: 'primary.main', fontWeight: 600,
                }}>
                  {item.ai_event_date || '—'}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.source_url ? (
                      <a href={item.source_url} target="_blank" rel="noopener noreferrer"
                         style={{ color: 'inherit' }}>
                        {item.title}
                      </a>
                    ) : item.title}
                  </Typography>
                  {item.ai_summary && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}>
                      {item.ai_summary}
                    </Typography>
                  )}
                  {item.ai_tags && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      {Object.entries(item.ai_tags).map(([k, v]) => v ? (
                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }} />
                      ) : null)}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

// ============================================================
// Tab 3：來源管理
// ============================================================
interface SourceFormData {
  name: string;
  source_type: SourceType;
  url: string;
  filter_keywords_text: string;  // CSV
  is_active: boolean;
}

const emptySourceForm: SourceFormData = {
  name: '', source_type: 'rss', url: '', filter_keywords_text: '', is_active: true,
};

const SourcesTab: React.FC<{ topic: Topic }> = ({ topic }) => {
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');
  const [sources, setSources] = useState<TopicSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<TopicSource | null>(null);
  const [form, setForm] = useState<SourceFormData>(emptySourceForm);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<TopicSource | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setSources(await listSources(topic.id)); }
    catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [topic.id, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptySourceForm);
    setEditOpen(true);
  };

  const openEdit = (s: TopicSource) => {
    setEditing(s);
    setForm({
      name: s.name,
      source_type: s.source_type,
      url: s.url,
      filter_keywords_text: (s.filter_keywords || []).join(', '),
      is_active: s.is_active,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const filter_keywords = form.filter_keywords_text
      .split(/[,，]/).map(s => s.trim()).filter(Boolean);
    const payload: SourceCreate = {
      name: form.name.trim(),
      source_type: form.source_type,
      url: form.url.trim(),
      filter_keywords: filter_keywords.length > 0 ? filter_keywords : undefined,
      is_active: form.is_active,
    };
    try {
      if (editing) {
        await updateSource(editing.id, payload);
        logUpdate(FUNC_CODE,
          { action: 'update_source', source_id: editing.id, ...editing },
          { action: 'update_source', source_id: editing.id, ...payload });
        showSuccess('SYS020002', { name: form.name, id: String(editing.id) });
      } else {
        const created = await createSource(topic.id, payload);
        logCreate(FUNC_CODE, { action: 'create_source', ...created });
        showSuccess('SYS020001', { name: form.name });
      }
      setEditOpen(false);
      reload();
    } catch (err: any) {
      if (editing) {
        logUpdate(FUNC_CODE, { source_id: editing.id, ...editing }, payload, String(err?.message || err));
      } else {
        logCreate(FUNC_CODE, { topic_id: topic.id, ...payload }, String(err?.message || err));
      }
      showApiError(err);
    }
    finally { setSaving(false); }
  };

  const handleTest = async (s: TopicSource) => {
    setTesting(s.id);
    const intent = { action: 'test_source', source_id: s.id, name: s.name, url: s.url };
    try {
      const updated = await testSource(s.id);
      setTestResult(updated);
      logUpdate(FUNC_CODE, intent,
        { ...intent, status: updated.test_status, message: updated.test_message });
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE, intent, intent, String(err?.message || err));
      showApiError(err);
    }
    finally { setTesting(null); }
  };

  const handleToggleActive = async (s: TopicSource) => {
    const intent = { action: 'toggle_source_active', source_id: s.id, name: s.name };
    try {
      await updateSource(s.id, { is_active: !s.is_active });
      logUpdate(FUNC_CODE,
        { ...intent, is_active: s.is_active },
        { ...intent, is_active: !s.is_active });
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE, intent, intent, String(err?.message || err));
      showApiError(err);
    }
  };

  const handleDelete = async (s: TopicSource) => {
    if (!window.confirm(`確定刪除來源「${s.name}」？`)) return;
    try {
      await deleteSource(s.id);
      logDelete(FUNC_CODE, { action: 'delete_source', source_id: s.id, ...s });
      reload();
    } catch (err: any) {
      logDelete(FUNC_CODE, { source_id: s.id, name: s.name }, String(err?.message || err));
      showApiError(err);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {canCreate && (
          <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={openCreate}>
            新增來源
          </Button>
        )}
        <Button size="small" variant="outlined"
                startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={reload} disabled={loading}>
          重新整理
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>名稱</TableCell>
              <TableCell width={80}>類型</TableCell>
              <TableCell>URL</TableCell>
              <TableCell width={100}>測試狀態</TableCell>
              <TableCell width={150}>上次抓取</TableCell>
              <TableCell width={70}>啟用</TableCell>
              <TableCell width={170} align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '載入中…' : '尚無來源'}
                </TableCell>
              </TableRow>
            ) : sources.map(s => (
              <TableRow key={s.id} hover>
                <TableCell>{s.name}</TableCell>
                <TableCell>
                  <Chip label={s.source_type} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{
                    fontFamily: 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                    maxWidth: 320,
                  }}>
                    {s.url}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.test_status} size="small"
                    color={s.test_status === 'passed' ? 'success'
                         : s.test_status === 'failed' ? 'error'
                         : s.test_status === 'testing' ? 'info' : 'default'}
                  />
                  {s.test_message && (
                    <Typography
                      variant="caption"
                      color={s.test_status === 'failed' ? 'error.main' : 'text.secondary'}
                      sx={{
                        display: 'block', mt: 0.25, maxWidth: 240,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        fontSize: '0.7rem',
                      }}
                    >
                      {s.test_message}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{fmt(s.last_fetch_at)}</Typography>
                  {s.last_fetch_count > 0 && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block' }}>
                      新增 {s.last_fetch_count} 筆
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Switch size="small" checked={s.is_active}
                          disabled={!canUpdate}
                          onChange={() => handleToggleActive(s)} />
                </TableCell>
                <TableCell align="right">
                  {canUpdate && (
                    <Tooltip title="測試">
                      <span>
                        <IconButton size="small" onClick={() => handleTest(s)}
                                    disabled={testing === s.id}>
                          {testing === s.id ? <CircularProgress size={16} />
                            : <TestIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canUpdate && (
                    <Tooltip title="編輯">
                      <IconButton size="small" onClick={() => openEdit(s)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="刪除">
                      <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增 / 編輯來源 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '編輯來源' : '新增來源'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="顯示名稱" required size="small"
                       value={form.name}
                       onChange={e => setForm({ ...form, name: e.target.value })} />
            <FormControl size="small" fullWidth>
              <InputLabel>類型</InputLabel>
              <Select label="類型" value={form.source_type}
                      onChange={e => setForm({ ...form, source_type: e.target.value as SourceType })}>
                <MenuItem value="rss">RSS</MenuItem>
                <MenuItem value="webpage">網頁列表頁</MenuItem>
              </Select>
            </FormControl>
            <TextField label="URL" required size="small"
                       value={form.url}
                       onChange={e => setForm({ ...form, url: e.target.value })}
                       helperText={editing ? '修改 URL 後建議重新「測試」確認' : ''} />
            <TextField label="粗篩關鍵字（可空）" size="small"
                       placeholder="逗號分隔"
                       value={form.filter_keywords_text}
                       onChange={e => setForm({ ...form, filter_keywords_text: e.target.value })}
                       helperText="僅當標題或摘要含任一關鍵字才入庫；空 = 全部交 AI 評分" />
            <FormControlLabel
              control={<Switch checked={form.is_active}
                              onChange={e => setForm({ ...form, is_active: e.target.checked })} />}
              label="啟用"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleSave}
                  disabled={saving || !form.name || !form.url}>
            {saving ? <CircularProgress size={18} /> : (editing ? '儲存' : '建立')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 測試結果 */}
      <Dialog open={!!testResult} onClose={() => setTestResult(null)}
              maxWidth="md" fullWidth>
        <DialogTitle>測試結果：{testResult?.name}</DialogTitle>
        <DialogContent dividers>
          {testResult && (
            <>
              <Alert severity={testResult.test_status === 'passed' ? 'success' : 'error'}
                     sx={{ mb: 2 }}>
                {testResult.test_message}
              </Alert>
              {testResult.test_sample && testResult.test_sample.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>標題</TableCell>
                        <TableCell width={140}>日期</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testResult.test_sample.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <a href={s.link} target="_blank" rel="noopener noreferrer">
                              {s.title}
                            </a>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{s.published || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestResult(null)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================
// Tab 4：報告
// ============================================================
const ReportsTab: React.FC<{ topic: Topic }> = ({ topic }) => {
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({
    title: '', report_type: 'custom', user_instruction: '',
    filter_dim: '', filter_value: '',
  });
  const [generating, setGenerating] = useState(false);
  const [items, setItems] = useState<ResearchItem[]>([]);
  const [viewing, setViewing] = useState<Report | null>(null);
  const [editContent, setEditContent] = useState('');
  const [reportEditMode, setReportEditMode] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setReports(await listReports(topic.id)); }
    catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [topic.id, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openGen = async () => {
    setGenForm({
      title: '', report_type: 'custom', user_instruction: '',
      filter_dim: '', filter_value: '',
    });
    try {
      setItems(await listItems(topic.id, { page_size: 200, ai_status: 'done' }));
    } catch (err) { showApiError(err); }
    setGenOpen(true);
  };

  // 報告生成對話框內可用的維度值（從目前 items 聚合）
  const genDimValues = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const dim of topic.tag_dimensions || []) map[dim] = new Set();
    for (const it of items) {
      const tags = mergedTags(it);
      for (const dim of topic.tag_dimensions || []) {
        const v = tags[dim];
        if (v) map[dim].add(String(v));
      }
    }
    return map;
  }, [items, topic.tag_dimensions]);

  // 依篩選後實際會送進報告的素材
  const filteredItems = useMemo(() => {
    if (!genForm.filter_dim || !genForm.filter_value) return items;
    return items.filter(it => {
      const tags = mergedTags(it);
      return String(tags[genForm.filter_dim] || '') === genForm.filter_value;
    });
  }, [items, genForm.filter_dim, genForm.filter_value]);

  const handleGenerate = async () => {
    setGenerating(true);
    const intent = {
      action: 'generate_report',
      topic_id: topic.id,
      title: genForm.title,
      report_type: genForm.report_type,
      item_count: filteredItems.length,
      user_instruction: genForm.user_instruction || undefined,
    };
    try {
      const r = await generateReport(topic.id, {
        title: genForm.title,
        report_type: genForm.report_type,
        item_ids: filteredItems.map(i => i.id),
        user_instruction: genForm.user_instruction || undefined,
      });
      logCreate(FUNC_CODE, { ...intent, report_id: r.id, total_tokens: r.total_tokens });
      showSuccess('SYS020001', { name: r.title });
      setGenOpen(false);
      reload();
      setViewing(r);
      setEditContent(r.content);
    } catch (err: any) {
      logCreate(FUNC_CODE, intent, String(err?.message || err));
      showApiError(err);
    }
    finally { setGenerating(false); }
  };

  const handleSaveEdit = async () => {
    if (!viewing) return;
    try {
      const updated = await updateReport(viewing.id, { content: editContent });
      logUpdate(FUNC_CODE,
        { action: 'edit_report', report_id: viewing.id, status: viewing.status,
          content_length: (viewing.content || '').length },
        { action: 'edit_report', report_id: viewing.id, status: updated.status,
          content_length: (editContent || '').length });
      setViewing(updated);
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE,
        { action: 'edit_report', report_id: viewing.id }, {}, String(err?.message || err));
      showApiError(err);
    }
  };

  const handleFinalize = async () => {
    if (!viewing) return;
    try {
      const updated = await updateReport(viewing.id, {
        content: editContent, status: 'final',
      });
      logUpdate(FUNC_CODE,
        { action: 'finalize_report', report_id: viewing.id, status: viewing.status },
        { action: 'finalize_report', report_id: viewing.id, status: 'final' });
      setViewing(updated);
      reload();
    } catch (err: any) {
      logUpdate(FUNC_CODE,
        { action: 'finalize_report', report_id: viewing.id }, {}, String(err?.message || err));
      showApiError(err);
    }
  };

  const handleDelete = async (r: Report) => {
    if (!window.confirm(`確定刪除報告「${r.title}」？`)) return;
    try {
      await deleteReport(r.id);
      logDelete(FUNC_CODE, { action: 'delete_report', report_id: r.id, title: r.title });
      reload();
    } catch (err: any) {
      logDelete(FUNC_CODE,
        { report_id: r.id, title: r.title }, String(err?.message || err));
      showApiError(err);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {canCreate && (
          <Button size="small" variant="contained" startIcon={<MagicIcon />}
                  onClick={openGen}>
            AI 生成報告
          </Button>
        )}
        <Button size="small" variant="outlined"
                startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={reload} disabled={loading}>
          重新整理
        </Button>
      </Stack>

      <Stack spacing={1}>
        {reports.length === 0 && !loading && (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            尚無報告
          </Typography>
        )}
        {reports.map(r => (
          <Card key={r.id} variant="outlined" sx={{ cursor: 'pointer' }}
                onClick={() => { setViewing(r); setEditContent(r.content); }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ReportIcon fontSize="small" color="action" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    <Typography component="span" sx={{
                      fontFamily: 'monospace', fontWeight: 600,
                      color: 'primary.main', mr: 1,
                    }}>
                      #{r.id}
                    </Typography>
                    {r.title}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                    <Chip label={r.report_type} size="small" variant="outlined" />
                    <Chip label={r.status} size="small"
                          color={r.status === 'final' ? 'success' : 'default'} />
                    <Typography variant="caption" color="text.secondary">
                      {fmt(r.created_at)}
                    </Typography>
                    {r.total_tokens && (
                      <Typography variant="caption" color="text.secondary">
                        {r.total_tokens} tokens
                      </Typography>
                    )}
                  </Stack>
                </Box>
                {canDelete && (
                  <Tooltip title="刪除">
                    <IconButton size="small" color="error"
                                onClick={(e) => { e.stopPropagation(); handleDelete(r); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* 生成對話框 */}
      <Dialog open={genOpen} onClose={() => setGenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>AI 生成報告</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="標題" required size="small"
                       value={genForm.title}
                       onChange={e => setGenForm({ ...genForm, title: e.target.value })} />
            <FormControl size="small" fullWidth>
              <InputLabel>類型</InputLabel>
              <Select label="類型" value={genForm.report_type}
                      onChange={e => setGenForm({ ...genForm, report_type: e.target.value })}>
                <MenuItem value="global_trend">全球走向</MenuItem>
                <MenuItem value="domestic_trend">國內走向</MenuItem>
                <MenuItem value="weekly">週報</MenuItem>
                <MenuItem value="custom">自訂</MenuItem>
              </Select>
            </FormControl>
            <TextField label="額外指示（可空）" multiline rows={3} size="small"
                       value={genForm.user_instruction}
                       onChange={e => setGenForm({ ...genForm, user_instruction: e.target.value })}
                       placeholder="例如：聚焦在跨境監管合作與 FATF Travel Rule" />

            {/* 標籤篩選素材 */}
            {(topic.tag_dimensions || []).length > 0 && (
              <>
                <Divider textAlign="left">
                  <Typography variant="caption" color="text.secondary">
                    依標籤篩選素材（不選 = 用全部）
                  </Typography>
                </Divider>
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>標籤維度</InputLabel>
                    <Select label="標籤維度" value={genForm.filter_dim}
                            onChange={e => setGenForm({ ...genForm, filter_dim: e.target.value, filter_value: '' })}>
                      <MenuItem value="">不篩選</MenuItem>
                      {(topic.tag_dimensions || []).map(d => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }} disabled={!genForm.filter_dim}>
                    <InputLabel>值</InputLabel>
                    <Select label="值" value={genForm.filter_value}
                            onChange={e => setGenForm({ ...genForm, filter_value: e.target.value })}>
                      <MenuItem value="">全部</MenuItem>
                      {genForm.filter_dim && Array.from(genDimValues[genForm.filter_dim] || []).sort().map(v => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </>
            )}

            <Alert severity={filteredItems.length === 0 ? 'warning' : 'info'}>
              {filteredItems.length === 0
                ? '篩選後無素材可用，請調整篩選條件'
                : (genForm.filter_dim && genForm.filter_value
                    ? `將使用 ${filteredItems.length} 筆篩選後素材（總 ${items.length}）生成報告`
                    : `將使用此議題目前 ${items.length} 筆已解析素材生成報告`)}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleGenerate}
                  disabled={generating || !genForm.title || filteredItems.length === 0}>
            {generating ? <CircularProgress size={18} /> : 'AI 生成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 報告檢視/編輯 */}
      <Dialog open={!!viewing} onClose={() => { setViewing(null); setReportEditMode(false); }}
              maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography component="span" sx={{
                fontFamily: 'monospace', fontWeight: 600,
                color: 'primary.main', mr: 1,
              }}>
                #{viewing?.id}
              </Typography>
              {viewing?.title}
              <Chip label={viewing?.status} size="small" sx={{ ml: 2 }}
                    color={viewing?.status === 'final' ? 'success' : 'default'} />
            </Box>
            <Button size="small"
                    variant={reportEditMode ? 'outlined' : 'contained'}
                    onClick={() => setReportEditMode(false)}>
              預覽
            </Button>
            {canUpdate && (
              <Button size="small"
                      variant={reportEditMode ? 'contained' : 'outlined'}
                      onClick={() => setReportEditMode(true)}>
                編輯
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {reportEditMode ? (
            <TextField
              multiline rows={20} fullWidth
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
          ) : (
            <Box sx={{ minHeight: 360 }}>
              {editContent
                ? <MarkdownText>{editContent}</MarkdownText>
                : <Typography color="text.secondary">（無內容）</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setViewing(null); setReportEditMode(false); }}>關閉</Button>
          <Button onClick={handleSaveEdit} disabled={!reportEditMode}>儲存</Button>
          <Button variant="contained" onClick={handleFinalize}
                  disabled={viewing?.status === 'final'}>
            定稿
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearchTopicDetailPage;